import React from "react";
import { motion } from "framer-motion";
import { Compass, Scale, Users, Briefcase, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Compass,
    title: "Operational risk frameworks",
    description:
      "Design, refresh and embed end-to-end operational risk frameworks — taxonomy, appetite, RCSAs, KRIs, incidents, scenarios — fit for modern organisations.",
  },
  {
    icon: Scale,
    title: "Third-Party Risk Management (TPRM)",
    description:
      "Pragmatic guidance on Third-Party Risk Management, supply chain resilience, intra-group arrangements, exit planning and testing, and supervisory dialogue — translating regulatory expectations into workable practice.",
  },
  {
    icon: Users,
    title: "Risk training & education",
    description:
      "Internationally recognised executive education and capability-building programmes, combining deep expertise with highly engaging delivery to embed risk management effectively.",
  },
  {
    icon: Briefcase,
    title: "Board & executive advisory",
    description:
      "Trusted counsel to Boards, Chairs, CROs and Risk Committees — providing strategic deep dives, risk culture assessments and capability uplifts.",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-16 md:py-24 bg-white border-t border-[#E5E5E5] overflow-hidden"
    >
      {/* Decorative oversized serif numeral as background flourish */}
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-10 right-[4%] font-serif-display text-[#9CDBD9]/15 text-[260px] md:text-[360px] leading-none italic"
      >
        04
      </span>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24 items-end">
          <div className="lg:col-span-7">
            <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-7 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#3E7373]" />
              Services
              <span className="font-serif-display italic text-[#3E7373]/70 normal-case tracking-normal text-[13px]">/ what I do</span>
            </p>
            <h2 className="font-serif-display text-[44px] sm:text-5xl lg:text-[72px] xl:text-[80px] leading-[0.98] tracking-tight text-[#0D0D0D] font-medium">
              Four practices.
              <br />
              <span className="italic text-[#3E7373] relative inline-block">
                One standard of excellence.
                <svg
                  aria-hidden="true"
                  viewBox="0 0 320 14"
                  preserveAspectRatio="none"
                  className="absolute left-0 -bottom-3 w-full h-3 text-[#9CDBD9]"
                >
                  <path
                    d="M2 9 C 70 1, 150 1, 210 7 S 300 11, 318 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h2>
            {/* Ornament rule */}
            <div className="mt-10 flex items-center gap-3" aria-hidden="true">
              <span className="block w-12 h-px bg-[#0D0D0D]" />
              <span className="block w-1.5 h-1.5 rotate-45 bg-[#9CDBD9]" />
              <span className="block w-24 h-px bg-[#0D0D0D]/15" />
            </div>
          </div>
          <p className="lg:col-span-5 font-sans-body text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Every engagement is shaped around the question that brought you here. The work below describes where I am most often asked to step in.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((svc, idx) => (
            <motion.div
              key={svc.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`service-card-${idx}`}
              className="group relative px-8 md:px-10 pt-12 pb-10 md:pt-14 md:pb-12 border border-[#E5E5E5] -ml-px -mt-px bg-white hover:bg-[#F9F8F6] hover:-translate-y-2 hover:shadow-[0_30px_60px_-32px_rgba(13,13,13,0.18)] transition-all duration-500"
            >
              {/* Top accent rule that reveals on hover */}
              <span
                aria-hidden="true"
                className="absolute top-0 left-0 h-[3px] w-0 bg-[#9CDBD9] group-hover:w-full transition-all duration-700 ease-out"
              />

              {/* Decorative oversized index numeral */}
              <span
                aria-hidden="true"
                className="absolute top-4 right-5 font-serif-display italic text-[64px] md:text-[80px] leading-none text-[#9CDBD9]/15 group-hover:text-[#9CDBD9]/40 transition-colors duration-700 select-none"
              >
                {String(idx + 1).padStart(2, "0")}
              </span>

              {/* Icon medallion */}
              <div className="relative inline-flex mb-10">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -m-1 rounded-full border border-[#9CDBD9]/50 opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                />
                <div className="relative w-14 h-14 rounded-full bg-[#F9F8F6] flex items-center justify-center text-[#3E7373] border border-[#0D0D0D]/10 group-hover:bg-white group-hover:text-[#0D0D0D] transition-colors duration-500">
                  <svc.icon size={22} strokeWidth={1.3} />
                </div>
              </div>

              {/* Eyebrow */}
              <p className="font-sans-body text-[10px] tracking-widest-2 uppercase text-[#3E7373] mb-4 flex items-center gap-2">
                <span className="block w-5 h-px bg-[#3E7373]" />
                Practice {String(idx + 1).padStart(2, "0")}
              </p>

              {/* Title */}
              <h3 className="font-serif-display text-[26px] md:text-[28px] text-[#0D0D0D] leading-[1.15] mb-5">
                {svc.title}
              </h3>

              {/* Description */}
              <p className="font-sans-body text-[15px] leading-[1.75] text-[#4A4A4A] mb-8">
                {svc.description}
              </p>

              {/* Foot CTA — arrow only */}
              <a
                href="#contact"
                aria-label={`Discuss ${svc.title}`}
                data-testid={`service-card-cta-${idx}`}
                className="inline-flex items-center justify-center w-10 h-10 text-[#0D0D0D] group-hover:text-[#3E7373] transition-colors duration-300"
              >
                <ArrowUpRight
                  size={22}
                  strokeWidth={1.4}
                  className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-500"
                />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
